import { useEffect, useMemo, useState } from 'react';
import { FileText, X } from 'lucide-react';
import { legalApi, type LegalDoc } from '../platform/api/legalApi';
import { LegalMarkdown } from '../platform/redesign/components/LegalDocsView';

export function PublicLegalLinks() {
  const [active, setActive] = useState<LegalDoc['key'] | null>(null);
  const [docs, setDocs] = useState<LegalDoc[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = (key: LegalDoc['key']) => {
    setActive(key);
    if (docs.length || loading) return;
    setLoading(true);
    setError(null);
    void legalApi.getPublicDocuments()
      .then((data) => setDocs(data.documents || []))
      .catch(() => setError('Не удалось загрузить документ. Попробуйте ещё раз.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!active) return;
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setActive(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active]);

  const current = useMemo(() => docs.find((doc) => doc.key === active) ?? docs[0], [active, docs]);

  return (
    <>
      <p className="oa-login-legal">
        Перед входом ознакомьтесь с{' '}
        <button type="button" onClick={() => open('tos')}>условиями использования</button>
        {' '}и{' '}
        <button type="button" onClick={() => open('privacy')}>политикой конфиденциальности</button>.
        Согласие будет запрошено отдельно при первом входе.
      </p>

      {active && (
        <div className="oa-public-legal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) setActive(null); }}>
          <section className="oa-public-legal-dialog" role="dialog" aria-modal="true" aria-label="Правовая информация">
            <header>
              <div><span><FileText /></span><div><small>До входа</small><strong>Правовая информация</strong></div></div>
              <button type="button" onClick={() => setActive(null)} aria-label="Закрыть"><X /></button>
            </header>
            {docs.length > 0 && (
              <nav role="tablist" aria-label="Документы">
                {docs.map((doc) => <button key={doc.key} type="button" role="tab" aria-selected={doc.key === current?.key} className={doc.key === current?.key ? 'is-active' : ''} onClick={() => setActive(doc.key)}>{doc.title}</button>)}
              </nav>
            )}
            <div className="oa-public-legal-scroll">
              {loading && <div className="oa-public-legal-state">Загружаем документ…</div>}
              {error && <div className="oa-public-legal-state error">{error}<button type="button" onClick={() => { setDocs([]); setLoading(false); open(active); }}>Повторить</button></div>}
              {!loading && !error && current && <><LegalMarkdown md={current.markdown} /><footer>Версия документа: {current.version}</footer></>}
            </div>
          </section>
        </div>
      )}
    </>
  );
}
